import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteQuery, useMutation } from '@tanstack/react-query'

import { useLatestRef } from '@shared/hooks/src/useLatestRef'
import useQuery from '@shared/hooks/src/useQuery'
import { queryClient, setQueriesData } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { filterCache, flatten, getErrorDetails, handleError, pageParam, QK } from '@shared/utils'

import {
  AttachmentStatus,
  filesFromDataTransfer,
  hasFiles,
  isTaskOwnedAttachments,
  resolveFilename,
  splitExtension,
  toStagedFile,
  toUploadPayload,
} from './TaskAttachments.utils'

const DOCUMENTS_LIMIT = 20

export function useTaskFiles(taskId) {
  const query = { sort: 'created_at', order: 'desc' }
  return useQuery({
    queryKey: QK.tasks.id(taskId).documents.list(query),
    queryFn: () => API.tasks.id(taskId).documents.list(query),
    enabled: Boolean(taskId),
  })
}

export function useTaskFileRemove(taskId) {
  const listsKey = { queryKey: QK.tasks.id(taskId).documents.lists }

  return useMutation({
    mutationFn: (fileId) => API.tasks.id(taskId).documents.id(fileId).remove(),
    onMutate: (fileId) => {
      const previous = queryClient.getQueriesData(listsKey)
      setQueriesData(
        listsKey,
        filterCache((d) => d.id !== fileId)
      )
      return { previous }
    },
    onError: (error, _fileId, context) => {
      context?.previous.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data))
      handleError(error)
    },
  })
}

export function usePatientDocuments(patientId, enabled = true) {
  const query = { order: 'desc', limit: DOCUMENTS_LIMIT }
  return useInfiniteQuery({
    queryKey: QK.patients.id(patientId).documents.list(query),
    queryFn: ({ pageParam }) => API.patients.id(patientId).documents.list({ ...query, offset: pageParam * DOCUMENTS_LIMIT }),
    select: flatten,
    initialPageParam: 0,
    getNextPageParam: pageParam(DOCUMENTS_LIMIT),
    enabled: Boolean(patientId) && enabled,
  })
}

export function useTaskFileLink(taskId) {
  return useMutation({
    mutationFn: async (documents) => {
      const failed = []
      for (const document of documents) {
        try {
          await API.tasks.id(taskId).documents.attach({ file_id: document.id, file_type: 'user' })
        } catch (error) {
          failed.push({ document, reason: getErrorDetails(error).join(' ') || 'Could not link' })
        }
      }
      return { linked: documents.length - failed.length, failed }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: QK.tasks.id(taskId).documents.lists }),
  })
}

export function useAttachmentUpload() {
  return useMutation({
    mutationFn: async ({ task, file, name = '' }) => {
      if (isTaskOwnedAttachments(task)) {
        const payload = await toUploadPayload(file, resolveFilename(file, name))
        return API.tasks.id(task.id).documents.attach({ file_type: 'upload', file: payload })
      }

      const customName = name.trim()
      const renamed = customName && customName !== splitExtension(file.name)[0]
      const payload = await toUploadPayload(file)
      return API.patients.id(task.patientId).documents.create({
        task_id: task.id,
        file: { ...payload, ...(renamed && { name: customName }), permission: 'admin', documenttype: 'Other' },
      })
    },
    onSuccess: (_data, { task }) => {
      queryClient.invalidateQueries({ queryKey: QK.tasks.id(task.id).documents.lists })
      if (!isTaskOwnedAttachments(task)) {
        queryClient.invalidateQueries({ queryKey: QK.patients.id(task.patientId).documents.lists })
      }
    },
  })
}

export function useAttachmentQueue() {
  const [items, setItems] = useState([])
  const [uploading, setUploading] = useState(false)
  const send = useAttachmentUpload()

  const patch = useCallback((id, changes) => setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...changes } : item))), [])

  const add = useCallback((files) => {
    setItems((prev) => {
      const known = new Set(prev.map((item) => item.id))
      const next = files.map(toStagedFile).filter((item) => !known.has(item.id))
      return next.length > 0 ? [...prev, ...next] : prev
    })
  }, [])

  const remove = useCallback((id) => setItems((prev) => prev.filter((item) => item.id !== id)), [])

  const clear = useCallback(() => setItems([]), [])

  const markPreviewed = useCallback((id) => patch(id, { previewed: true }), [patch])

  const setName = useCallback((id, name) => patch(id, { name }), [patch])

  const upload = useCallback(
    async (task) => {
      const pending = items.filter((item) => item.status !== AttachmentStatus.Uploading && !item.reason)

      setUploading(true)
      let failed = 0

      for (const item of pending) {
        patch(item.id, { status: AttachmentStatus.Uploading })
        try {
          await send.mutateAsync({ task, file: item.file, name: item.name })
          remove(item.id)
        } catch (error) {
          failed += 1
          patch(item.id, { status: AttachmentStatus.Error, reason: getErrorDetails(error).join(' ') || 'Upload failed' })
        }
      }

      setUploading(false)

      return { uploaded: pending.length - failed, failed }
    },
    [items, patch, remove, send]
  )

  const retry = useCallback((id) => patch(id, { status: AttachmentStatus.Staged, reason: null }), [patch])

  const ready = useMemo(() => items.filter((item) => !item.reason && item.status !== AttachmentStatus.Uploading), [items])
  const previewed = useMemo(() => ready.filter((item) => item.previewed), [ready])
  const allPreviewed = ready.length > 0 && previewed.length === ready.length

  return { items, add, remove, clear, markPreviewed, setName, retry, upload, uploading, ready, previewed, allPreviewed }
}

export function useFileIntake({ rootRef, onFiles, disabled = false }) {
  const [dragging, setDragging] = useState(false)
  const [scope, setScope] = useState(null)
  const depth = useRef(0)
  const onFilesRef = useLatestRef(onFiles)

  useEffect(() => {
    const root = rootRef.current
    if (!root || disabled) return undefined

    const dialog = root.closest('[role="dialog"]')
    const listenRoot = dialog ?? document.body
    setScope(dialog ?? root)

    const inScope = (target) => {
      if (!dialog && document.querySelector('[role="dialog"]')) return false
      return listenRoot.contains(target)
    }

    const onPaste = (e) => {
      if (!inScope(e.target) || e.target.isContentEditable) return
      const files = filesFromDataTransfer(e.clipboardData)
      if (files.length === 0) return
      e.preventDefault()
      onFilesRef.current(files)
    }

    const onDragEnter = (e) => {
      if (!hasFiles(e.dataTransfer) || !inScope(e.target)) return
      depth.current += 1
      setDragging(true)
    }

    const onDragOver = (e) => {
      if (!hasFiles(e.dataTransfer) || !inScope(e.target)) return
      e.preventDefault()
    }

    const onDragLeave = (e) => {
      if (!hasFiles(e.dataTransfer) || !inScope(e.target)) return
      depth.current = Math.max(depth.current - 1, 0)
      if (depth.current === 0) setDragging(false)
    }

    const onDrop = (e) => {
      if (!inScope(e.target)) return
      depth.current = 0
      setDragging(false)
      const files = filesFromDataTransfer(e.dataTransfer)
      if (files.length === 0) return
      e.preventDefault()
      onFilesRef.current(files)
    }

    document.addEventListener('paste', onPaste)
    document.addEventListener('dragenter', onDragEnter)
    document.addEventListener('dragover', onDragOver)
    document.addEventListener('dragleave', onDragLeave)
    document.addEventListener('drop', onDrop)

    return () => {
      document.removeEventListener('paste', onPaste)
      document.removeEventListener('dragenter', onDragEnter)
      document.removeEventListener('dragover', onDragOver)
      document.removeEventListener('dragleave', onDragLeave)
      document.removeEventListener('drop', onDrop)
    }
  }, [rootRef, onFilesRef, disabled])

  return { dragging, scope }
}
